import { Box } from "../../box";
import { forwardRef } from "react";
import { cssModule, isNil, mergeProps, useControllableState, useEventCallback, useId } from "../../shared";
import { useResponsiveValue } from "../../styling";
import { TabList } from "./TabList";
import { TabPanels } from "./TabPanels";
import { TabsContext } from "./TabsContext";
import { useMemo } from "react";
import { useTabsItems } from "./useTabsItems";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DefaultElement = "div";
export function InnerTabs({
  as = DefaultElement,
  "aria-label": ariaLabel,
  autoFocus,
  children,
  defaultSelectedKey,
  fluid,
  forwardedRef,
  id,
  manual,
  onSelectionChange,
  orientation = "horizontal",
  selectedKey: selectedKeyProp,
  ...rest
}) {
  const fluidValue = useResponsiveValue(fluid);
  const orientationValue = useResponsiveValue(orientation);
  const [selectedKey, setSelectedKey] = useControllableState(selectedKeyProp, defaultSelectedKey, "0");
  const [tabs, panels] = useTabsItems(children, useId(id, "o-ui-tabs"));
  const handleSelect = useEventCallback((event, newKey) => {
    if (newKey !== selectedKey) {
      setSelectedKey(newKey);

      if (!isNil(onSelectionChange)) {
        onSelectionChange(event, newKey);
      }
    }
  }); // Ensure the selected key match a valid tab which is not disabled.

  const adjustedKey = useMemo(() => {
    var _selectedTab$props;

    const selectedTab = tabs.find(x => x.key === selectedKey);

    if (isNil(selectedTab)) {
      return tabs[0].key;
    }

    if ((_selectedTab$props = selectedTab.props) !== null && _selectedTab$props !== void 0 && _selectedTab$props.disabled) {
      var _tabs$find$key, _tabs$find;

      return (_tabs$find$key = (_tabs$find = tabs.find(x => {
        var _x$props;

        return !((_x$props = x.props) !== null && _x$props !== void 0 && _x$props.disabled);
      })) === null || _tabs$find === void 0 ? void 0 : _tabs$find.key) !== null && _tabs$find$key !== void 0 ? _tabs$find$key : tabs[0].key;
    }

    return selectedKey;
  }, [selectedKey, tabs]);
  return /*#__PURE__*/_jsx(Box, { ...mergeProps(rest, {
      as,
      className: cssModule("o-ui-tabs", fluidValue && "fluid", orientationValue),
      id,
      ref: forwardedRef
    }),
    children: /*#__PURE__*/_jsxs(TabsContext.Provider, {
      value: {
        isManual: manual,
        onSelect: handleSelect,
        orientation: orientationValue,
        selectedKey: adjustedKey
      },
      children: [/*#__PURE__*/_jsx(TabList, {
        "aria-label": ariaLabel,
        autoFocus: autoFocus,
        tabs: tabs
      }), /*#__PURE__*/_jsx(TabPanels, {
        panels: panels
      })]
    })
  });
}
InnerTabs.defaultElement = DefaultElement;
export const Tabs = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerTabs, { ...props,
  forwardedRef: ref
}));